import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Paper, Select, MenuItem, FormControl, InputLabel, Box, Accordion, AccordionSummary, AccordionDetails, Grid, Checkbox, FormControlLabel, Snackbar, Alert } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import config from '../config/config'; // Import configuration

const UserComponent = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetails, setUserDetails] = useState({
    id: '',
    username: '',
    email: '',
    password: '',
    accountExpired: false,
    accountLocked: false,
    credentialsExpired: false,
    enabled: true,
    twoFactorEnabled: false,
    roles: [],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [gridApi, setGridApi] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('authToken');
        console.log('Fetching users with token:', token);

        const usersResponse = await axios.get(`${config.apiBaseUrl}/api/users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
          withCredentials: true, // Include credentials in the request
        });

        console.log('Users response:', usersResponse);

        setUsers(usersResponse.data);
        setFilteredUsers(usersResponse.data);

        // Highlight the first row by default
        if (usersResponse.data.length > 0) {
          handleRowClick(usersResponse.data[0]);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setSnackbar({ open: true, message: 'Failed to fetch users. Please try again later.', severity: 'error' });
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleRoleChange = (event) => {
    const {
      target: { value, checked },
    } = event;
    setSelectedRoles((prevSelectedRoles) =>
      checked ? [...prevSelectedRoles, parseInt(value)] : prevSelectedRoles.filter((role) => role !== parseInt(value))
    );
  };

  const handleRowClick = (user) => {
    setUserDetails({
      id: user.id,
      username: user.username,
      email: user.email,
      password: '', // Password should not be populated for security reasons
      accountExpired: user.accountExpired,
      accountLocked: user.accountLocked,
      credentialsExpired: user.credentialsExpired,
      enabled: user.enabled,
      twoFactorEnabled: user.twoFactorEnabled,
      roles: user.roles,
    });
    setSelectedRoles(user.roles.map(role => role.id));
    setSelectedUserId(user.id);
    setExpanded(true); // Expand the accordion
  };

  const handleNewUser = () => {
    setUserDetails({
      id: '',
      username: '',
      email: '',
      password: '',
      accountExpired: false,
      accountLocked: false,
      credentialsExpired: false,
      enabled: true,
      twoFactorEnabled: false,
      roles: [],
    });
    setSelectedRoles([]);
    setSelectedUserId(null);
    setExpanded(true); // Expand the accordion
  };

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userPayload = {
        id: userDetails.id,
        username: userDetails.username,
        email: userDetails.email,
        accountExpired: userDetails.accountExpired,
        accountLocked: userDetails.accountLocked,
        credentialsExpired: userDetails.credentialsExpired,
        enabled: userDetails.enabled,
        twoFactorEnabled: userDetails.twoFactorEnabled,
        roles: selectedRoles.map(roleId => ({ id: roleId })),
      };
      if (userDetails.password) {
        userPayload.password = userDetails.password; // Include password only if it's set
      }
      if (userDetails.id) {
        // Update existing user
        await axios.put(`${config.apiBaseUrl}/api/users/${userDetails.id}`, userPayload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
          withCredentials: true, // Include credentials in the request
        });
        setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
      } else {
        // Create new user
        await axios.post(`${config.apiBaseUrl}/api/users`, userPayload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
          withCredentials: true, // Include credentials in the request
        });
        setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
      }
      // Refresh user list
      const usersResponse = await axios.get(`${config.apiBaseUrl}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
        withCredentials: true, // Include credentials in the request
      });
      setUsers(usersResponse.data);
      setFilteredUsers(usersResponse.data);
    } catch (error) {
      console.error('Error saving user:', error);
      setSnackbar({ open: true, message: 'Failed to save user', severity: 'error' });
    }
  };

  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${config.apiBaseUrl}/api/users/${userDetails.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
        withCredentials: true, // Include credentials in the request
      });
      setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
      // Refresh user list
      const usersResponse = await axios.get(`${config.apiBaseUrl}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-TenantID': 'admin',
        },
        withCredentials: true, // Include credentials in the request
      });
      setUsers(usersResponse.data);
      setFilteredUsers(usersResponse.data);
      // Clear user details
      setUserDetails({
        id: '',
        username: '',
        email: '',
        password: '',
        accountExpired: false,
        accountLocked: false,
        credentialsExpired: false,
        enabled: true,
        twoFactorEnabled: false,
        roles: [],
      });
      setSelectedRoles([]);
      setSelectedUserId(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({ open: true, message: 'Failed to delete user', severity: 'error' });
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    if (gridApi) {
      gridApi.setQuickFilter(event.target.value);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columnDefs = [
    { headerName: 'Username', field: 'username', sortable: true, filter: true, minWidth: 150 },
    { headerName: 'Email', field: 'email', sortable: true, filter: true, minWidth: 200 },
    { headerName: 'Account Expired', field: 'accountExpired', sortable: true, filter: true, cellRenderer: (params) => (params.value ? 'Yes' : 'No'), minWidth: 150 },
    { headerName: 'Account Locked', field: 'accountLocked', sortable: true, filter: true, cellRenderer: (params) => (params.value ? 'Yes' : 'No'), minWidth: 150 },
    { headerName: 'Credentials Expired', field: 'credentialsExpired', sortable: true, filter: true, cellRenderer: (params) => (params.value ? 'Yes' : 'No'), minWidth: 150 },
    { headerName: 'Roles', field: 'roles', sortable: true, filter: true, cellRenderer: (params) => params.value.map((role) => role.name).join(', '), minWidth: 200 },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 150,
    filter: true,
    sortable: true,
    resizable: true,
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    if (params.api.getDisplayedRowCount() > 0) {
      const firstRow = params.api.getDisplayedRowAtIndex(0);
      params.api.selectNode(firstRow, true);
      handleRowClick(firstRow.data);
    }
  };

  return (
    <Box>
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ marginBottom: 2 }}
      />
      <Paper elevation={3} sx={{ padding: 2 }}>
        <Box className="ag-theme-alpine" sx={{ height: 400, width: '100%' }}>
          <AgGridReact
            columnDefs={columnDefs}
            rowData={filteredUsers}
            pagination={true}
            paginationPageSize={10}
            onRowClicked={(event) => handleRowClick(event.data)}
            onGridReady={onGridReady}
            defaultColDef={defaultColDef}
          />
        </Box>
      </Paper>
      <Accordion expanded={expanded} onChange={() => setExpanded(true)} sx={{ marginTop: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">User Management</Typography>
          <Box ml={2} display="flex" gap={2}>
            <Button variant="contained" color="primary" onClick={handleSaveUser} disabled={!userDetails.username || !userDetails.email || (!userDetails.id && !userDetails.password)}>
              Save
            </Button>
            <Button variant="contained" color="secondary" onClick={handleDeleteUser} disabled={!userDetails.id}>
              Delete
            </Button>
            <Button variant="contained" color="default" onClick={handleNewUser}>
              New
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                value={userDetails.username}
                onChange={(e) => setUserDetails({ ...userDetails, username: e.target.value })}
                InputProps={{
                  readOnly: !!userDetails.id,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={userDetails.email}
                onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                InputProps={{
                  readOnly: !!userDetails.id,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                value={userDetails.password}
                onChange={(e) => setUserDetails({ ...userDetails, password: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Account Expired</InputLabel>
                <Select
                  value={userDetails.accountExpired ? 'Yes' : 'No'}
                  onChange={(e) => setUserDetails({ ...userDetails, accountExpired: e.target.value === 'Yes' })}
                  label="Account Expired"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Account Locked</InputLabel>
                <Select
                  value={userDetails.accountLocked ? 'Yes' : 'No'}
                  onChange={(e) => setUserDetails({ ...userDetails, accountLocked: e.target.value === 'Yes' })}
                  label="Account Locked"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Credentials Expired</InputLabel>
                <Select
                  value={userDetails.credentialsExpired ? 'Yes' : 'No'}
                  onChange={(e) => setUserDetails({ ...userDetails, credentialsExpired: e.target.value === 'Yes' })}
                  label="Credentials Expired"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Enabled</InputLabel>
                <Select
                  value={userDetails.enabled ? 'Yes' : 'No'}
                  onChange={(e) => setUserDetails({ ...userDetails, enabled: e.target.value === 'Yes' })}
                  label="Enabled"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Two Factor Enabled</InputLabel>
                <Select
                  value={userDetails.twoFactorEnabled ? 'Yes' : 'No'}
                  onChange={(e) => setUserDetails({ ...userDetails, twoFactorEnabled: e.target.value === 'Yes' })}
                  label="Two Factor Enabled"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box border={1} borderColor="grey.400" borderRadius={4} padding={2} marginTop={2}>
                <Typography variant="subtitle1">Select Roles</Typography>
                <Box display="flex" flexDirection="column">
                  <FormControlLabel
                    control={<Checkbox checked={selectedRoles.includes(2)} onChange={handleRoleChange} value={2} />}
                    label="ROLE_ADMIN"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={selectedRoles.includes(1)} onChange={handleRoleChange} value={1} />}
                    label="ROLE_USER"
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserComponent;